import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { DollarSign, Loader2, Download } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Payroll() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => { fetchPayroll(); }, []);

  const fetchPayroll = async () => {
    try {
      const { data: d } = await axios.get(`${API}/payroll`, { withCredentials: true });
      setData(d);
    } catch {} finally { setLoading(false); }
  };

  const handleProcess = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/payroll/process`, { month: "April 2026" }, { withCredentials: true });
      fetchPayroll();
    } catch {} finally { setProcessing(false); }
  };

  const totalNet = data.reduce((sum, d) => sum + (d.net_salary || 0), 0);
  const totalBasic = data.reduce((sum, d) => sum + (d.basic || 0), 0);

  return (
    <div className="space-y-6" data-testid="payroll-module">
      <div className="flex items-start justify-between border-b border-[#D8D4CB] pb-5">
        <div>
          <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Finance & HR</p>
          <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">Payroll Management</h2>
        </div>
        <button onClick={handleProcess} disabled={processing} data-testid="process-payroll-btn"
          className="bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-[10px] uppercase tracking-widest px-5 py-3 hover:bg-[#1C1C22] transition-colors disabled:opacity-50 flex items-center gap-2">
          {processing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <DollarSign className="w-3.5 h-3.5" />}
          Process April 2026
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          { label: "Total Disbursed", value: `₹${(totalNet / 100000).toFixed(1)}L`, color: "#22C55E" },
          { label: "Total Basic", value: `₹${(totalBasic / 100000).toFixed(1)}L`, color: "#0A7AFF" },
          { label: "Employees", value: data.length, color: "#F59E0B" },
        ].map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-white border border-[#E2DDD4] p-5" style={{ borderLeft: `3px solid ${s.color}` }}>
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-2">{s.label}</p>
            <p className="font-['JetBrains_Mono'] font-bold text-3xl text-[#0C0C10]">{s.value}</p>
          </motion.div>
        ))}
      </div>

      <div className="bg-white border border-[#E2DDD4]">
        <div className="grid gap-0 px-5 py-3 border-b border-[#E2DDD4] bg-[#FAFAF7]" style={{ gridTemplateColumns: "1.5fr 1fr 80px 80px 80px 80px 100px 60px" }}>
          {["Employee", "Department", "Basic", "HRA", "DA", "PF", "Net Salary", ""].map(h => (
            <span key={h} className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8]">{h}</span>
          ))}
        </div>
        {loading ? <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#BDBAA8]" /></div>
          : data.map((item, i) => (
          <div key={item._id} className="grid gap-0 px-5 py-3.5 border-b border-[#F0ECE4] last:border-0 hover:bg-[#FAFAF7] items-center" style={{ gridTemplateColumns: "1.5fr 1fr 80px 80px 80px 80px 100px 60px" }}>
            <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{item.employee_name}</p>
            <span className="font-['DM_Sans'] text-xs text-[#7A7468]">{item.department}</span>
            <span className="font-['JetBrains_Mono'] text-xs text-[#0C0C10]">₹{(item.basic/1000).toFixed(0)}K</span>
            <span className="font-['JetBrains_Mono'] text-xs text-[#0C0C10]">₹{(item.hra/1000).toFixed(0)}K</span>
            <span className="font-['JetBrains_Mono'] text-xs text-[#0C0C10]">₹{(item.da/1000).toFixed(0)}K</span>
            <span className="font-['JetBrains_Mono'] text-xs text-[#FF3636]">-₹{(item.pf_deduction/1000).toFixed(0)}K</span>
            <span className="font-['JetBrains_Mono'] text-sm font-bold text-[#22C55E]">₹{(item.net_salary/1000).toFixed(1)}K</span>
            <button
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/payslip?employee_name=${encodeURIComponent(item.employee_name)}&month=${encodeURIComponent(item.month)}`, "_blank")}
              className="p-1.5 text-[#0A7AFF] hover:bg-[#0A7AFF]/10 transition-colors border border-[#0A7AFF]/20" data-testid={`download-slip-${i}`}>
              <Download className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
